import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Table, Input, Typography, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetHseWorkPermits } from '../../hooks/genericHooks';
import type { HseWorkPermit } from '../../types';

interface WPermitSelectProps {
  open: boolean;
  value?: string; // preselected ref_num (optional)
  onOk: (selected: [string, string, string, string]) => void; // [ref_num, description, activity_code, activity_name]
  onCancel: () => void;
  title?: string;
}

interface WPermitSelectContentProps {
  value?: string;
  onOk: (selected: [string, string, string, string]) => void;
  onCancel: () => void;
}

const WPermitSelectContent: React.FC<WPermitSelectContentProps> = ({ value, onOk, onCancel }) => {
  const { data, isLoading } = useGetHseWorkPermits();
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(value ?? null);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setSelectedRowKey(value ?? null);
  }, [value]);

  const source: HseWorkPermit[] = useMemo(() => data?.data ?? [], [data]);

  const tokens = useMemo(() => search.toLowerCase().split(/\s+/).map(t => t.trim()).filter(Boolean), [search]);

  const filteredData = useMemo(() => {
    if (tokens.length === 0) return source;
    return (source || []).filter((item) => {
      const hay = [
        item.ref_num ?? '',
        item.description ?? '',
        item.activity_code ?? '',
        item.activity_name ?? '',
        item.location ?? '',
        item.issuer ?? '',
        item.company_code ?? '',
        item.status ?? '',
      ].join(' \u0000 ').toLowerCase();
      return tokens.every(tok => hay.includes(tok));
    });
  }, [source, tokens]);

  const columns: ColumnsType<HseWorkPermit> = [
    { title: 'Ref Num', dataIndex: 'ref_num', key: 'ref_num', width: 140 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Activity Code', dataIndex: 'activity_code', key: 'activity_code', width: 140 },
    { title: 'Activity Name', dataIndex: 'activity_name', key: 'activity_name', ellipsis: true },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110 },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        Double-click a row to select a Work Permit
      </Typography.Text>
      <Input.Search
        placeholder="Search work permits..."
        allowClear
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <Table<HseWorkPermit>
        rowKey={record => String(record.id)}
        loading={isLoading}
        dataSource={filteredData}
        columns={columns}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (_c, size) => setPageSize(size),
        }}
        onRow={record => ({
          onClick: () => setSelectedRowKey(String(record.id)),
          onDoubleClick: () => {
            setSelectedRowKey(String(record.id));
            const refNum = String(record.ref_num ?? '').trim();
            const desc = String(record.description ?? '').trim();
            const actCode = String(record.activity_code ?? '').trim();
            const actName = String(record.activity_name ?? '').trim();
            onOk([refNum, desc, actCode, actName]);
            onCancel();
          },
          style: {
            cursor: 'pointer',
            background: selectedRowKey === String(record.id) ? '#e6f7ff' : undefined,
          },
        })}
        components={{
          body: {
            row: (props: any) => (
              <tr
                {...props}
                style={{
                  ...props.style,
                  lineHeight: 1,
                  padding: 0,
                  borderBottom: '1px solid #f0f0f0',
                  height: 28,
                }}
              />
            ),
            cell: (props: any) => (
              <td
                {...props}
                style={{
                  ...props.style,
                  padding: '0 8px',
                  borderBottom: 'none',
                  height: 28,
                  verticalAlign: 'middle',
                }}
              />
            ),
          },
        }}
        style={{ margin: 0 }}
        showHeader={true}
        bordered={false}
        size="small"
      />
    </Space>
  );
};

const WPermitSelect: React.FC<WPermitSelectProps> = ({ open, value, onOk, onCancel, title = 'Select Work Permit' }) => {
  return (
    <Modal open={open} onCancel={onCancel} title={title} footer={null} destroyOnClose width={900}>
      <WPermitSelectContent value={typeof value === 'string' ? value : ''} onOk={onOk} onCancel={onCancel} />
    </Modal>
  );
};

export default WPermitSelect;
export { WPermitSelectContent };
